/**
 * wastewaterAnalyticsService.ts
 *
 * Extended analytics for the Wastewater sector page:
 *   - Population-scaled time series (2015–2024)
 *   - Statewide per-capita percentile
 *   - Peer city comparison
 *   - Treatment activity breakdown
 *
 * All functions are cached in-memory per session to avoid redundant queries.
 */

import { supabaseClient } from './supabase';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WastewaterTimeSeriesPoint {
  year: number;
  emissions_mtco2e: number | null;
  per_capita: number | null;
  population: number | null;
  /** true = modelled from 2020 baseline × population ratio; false = directly measured */
  isEstimated: boolean;
}

export interface WastewaterTimeSeries {
  points: WastewaterTimeSeriesPoint[];
  baselineYear: number | null;
  baselineEmissions: number | null;
  /** Percent change from first to last available point, or null */
  pctChange: number | null;
  /** Whether the series is entirely estimated (Tier 5) */
  allEstimated: boolean;
}

export interface WastewaterPercentileResult {
  /** Per-capita emissions for this jurisdiction (MTCO₂e / person) */
  value: number | null;
  /** Fraction of California cities with LOWER per-capita wastewater emissions, 0–1 */
  percentile: number | null;
  /** Statewide median per-capita wastewater emissions */
  stateMedian: number | null;
  /** Number of cities used for comparison */
  comparisonCount: number;
}

export interface WastewaterPeerCity {
  location_name: string;
  population: number | null;
  wastewater_per_capita: number | null;
  wastewater_total: number | null;
  data_tier: number | null;
  geofips: string;
}

export interface WastewaterTreatmentPoint {
  treatment_level: string;
  volume_af: number;
  emissions_mtco2e: number;
  share: number;
}

// ─── Caches ──────────────────────────────────────────────────────────────────

const timeSeriesCache = new Map<string, Promise<WastewaterTimeSeries>>();
const percentileCache = new Map<string, Promise<WastewaterPercentileResult>>();
const peerCache = new Map<string, Promise<WastewaterPeerCity[]>>();
const treatmentCache = new Map<string, Promise<WastewaterTreatmentPoint[]>>();

// ─── Time Series ─────────────────────────────────────────────────────────────

/**
 * Build a 2005–2024 wastewater emissions time series.
 *
 * Strategy:
 *   1. Fetch service population by year from `service_population` table
 *      (residential pop + workplace employment). Falls back to `population`
 *      (residential only) for years/places where LODES data is absent.
 *   2. Fetch the 2020 measured wastewater emissions from v_wastewater_inventory_summary.
 *   3. For every year in the service population table, scale:
 *        emissions(y) = emissions_2020 × (service_pop(y) / service_pop_2020)
 *   4. Actual measured rows (if multiple years exist in the summary) override estimates.
 *
 * Service population is the correct denominator for wastewater because treatment
 * plants serve both residents and employees of non-residential customers.
 */
export async function fetchWastewaterTimeSeries(
  paddedGeofips: string,
  measuredEmissionsByYear: Map<number, number>,
  measuredPerCapitaByYear: Map<number, number | null>
): Promise<WastewaterTimeSeries> {
  if (timeSeriesCache.has(paddedGeofips)) return timeSeriesCache.get(paddedGeofips)!;
  const promise = _fetchWastewaterTimeSeriesUncached(
    paddedGeofips,
    measuredEmissionsByYear,
    measuredPerCapitaByYear
  );
  timeSeriesCache.set(paddedGeofips, promise);
  return promise;
}

async function _fetchWastewaterTimeSeriesUncached(
  paddedGeofips: string,
  measuredEmissionsByYear: Map<number, number>,
  measuredPerCapitaByYear: Map<number, number | null>
): Promise<WastewaterTimeSeries> {
  const empty: WastewaterTimeSeries = {
    points: [],
    baselineYear: null,
    baselineEmissions: null,
    pctChange: null,
    allEstimated: true,
  };

  // Prefer service_population (residential + employment). Fall back to residential-only
  // population table if service_population data is not yet available for this geofips.
  const shortGeofips = String(Number(paddedGeofips)); // strip leading zero for population table
  const { data: svcPopData } = await supabaseClient
    .from('service_population')
    .select('year, service_pop, residential_pop, has_employment_data')
    .eq('geofips', paddedGeofips)
    .gte('year', 2005)
    .lte('year', 2024)
    .order('year', { ascending: true });

  let popByYear: Map<number, number>;

  // Track service_pop (for scaling) and residential_pop (for per-capita display) separately.
  // "Per capita" shown in charts should always be per resident, not per service-population unit.
  // Service population is only used as the scaling denominator so that commercial/industrial
  // wastewater generation is captured in the trend — not as the displayed denominator.
  let resByYear = new Map<number, number>(); // residential population by year

  if (svcPopData && svcPopData.length > 0) {
    popByYear = new Map<number, number>(
      svcPopData.map((r) => [Number(r.year), Number(r.service_pop)])
    );
    resByYear = new Map<number, number>(
      svcPopData.map((r) => [Number(r.year), Number(r.residential_pop)])
    );
  } else {
    // Fallback: residential population only
    const { data: popData } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', Number(shortGeofips))
      .gte('year', 2005)
      .lte('year', 2024)
      .order('year', { ascending: true });

    if (!popData || popData.length === 0) return empty;
    popByYear = new Map<number, number>(
      popData.map((r) => [Number(r.year), Number(r.population)])
    );
    resByYear = popByYear; // same in fallback case
  }

  if (popByYear.size === 0) return empty;

  // Baseline: use 2020 measured row, or fall back to the first available measured year
  const baseline2020Emissions = measuredEmissionsByYear.get(2020) ?? null;
  const baseline2020Pop = popByYear.get(2020) ?? null;

  if (!baseline2020Emissions || !baseline2020Pop) {
    // No baseline — can't generate time series
    return empty;
  }

  const points: WastewaterTimeSeriesPoint[] = [];

  for (const [year, servicePop] of Array.from(popByYear.entries()).sort((a, b) => a[0] - b[0])) {
    const resPop = resByYear.get(year) ?? servicePop;
    const measured = measuredEmissionsByYear.get(year);
    if (measured != null) {
      // Use the actual measured value.
      // Per-capita: prefer the DB-stored value (computed against residential pop at load time);
      // fall back to measured / residential_pop so the denominator is consistent with estimates.
      points.push({
        year,
        emissions_mtco2e: measured,
        per_capita: measuredPerCapitaByYear.get(year) ?? (resPop > 0 ? measured / resPop : null),
        population: resPop,
        isEstimated: false,
      });
    } else {
      // Scale from 2020 baseline using service population ratio (captures employment trends).
      // Display per-capita against residential population for user-facing consistency.
      const scaledEmissions = baseline2020Emissions * (servicePop / baseline2020Pop);
      points.push({
        year,
        emissions_mtco2e: scaledEmissions,
        per_capita: resPop > 0 ? scaledEmissions / resPop : null,
        population: resPop,
        isEstimated: true,
      });
    }
  }

  const first = points[0];
  const last = points[points.length - 1];
  const pctChange =
    first?.emissions_mtco2e && last?.emissions_mtco2e
      ? ((last.emissions_mtco2e - first.emissions_mtco2e) / first.emissions_mtco2e) * 100
      : null;

  return {
    points,
    baselineYear: 2020,
    baselineEmissions: baseline2020Emissions,
    pctChange,
    allEstimated: points.every((p) => p.isEstimated),
  };
}

export function clearWastewaterTimeSeriesCache(): void {
  timeSeriesCache.clear();
}

// ─── Statewide Percentile ─────────────────────────────────────────────────────

export async function fetchWastewaterStatewidePercentile(
  paddedGeofips: string
): Promise<WastewaterPercentileResult> {
  if (percentileCache.has(paddedGeofips)) return percentileCache.get(paddedGeofips)!;
  const promise = _fetchPercentileUncached(paddedGeofips);
  percentileCache.set(paddedGeofips, promise);
  return promise;
}

async function _fetchPercentileUncached(
  paddedGeofips: string
): Promise<WastewaterPercentileResult> {
  const empty: WastewaterPercentileResult = {
    value: null,
    percentile: null,
    stateMedian: null,
    comparisonCount: 0,
  };

  // Use v_wastewater_inventory_summary — has wastewater_emissions_per_capita pre-computed
  // and avoids querying the non-existent population column on ghg_inventory_precalculated.
  const { data } = await supabaseClient
    .from('v_wastewater_inventory_summary')
    .select('geofips,wastewater_emissions_mtco2e,wastewater_emissions_per_capita')
    .eq('inventory_year', 2020)
    .not('wastewater_emissions_per_capita', 'is', null)
    .gt('wastewater_emissions_per_capita', 0);

  if (!data || data.length === 0) return empty;

  const values: number[] = data
    .map((r) => {
      const v = Number(r.wastewater_emissions_per_capita);
      return Number.isFinite(v) && v > 0 ? v : null;
    })
    .filter((v): v is number => v != null);

  if (values.length === 0) return empty;

  values.sort((a, b) => a - b);
  const stateMedian = values[Math.floor(values.length / 2)] ?? null;

  // Find this jurisdiction's value
  const myRow = data.find(
    (r) => String(r.geofips).padStart(7, '0') === paddedGeofips
  );
  let myValue: number | null = null;
  if (myRow?.wastewater_emissions_per_capita != null) {
    myValue = Number(myRow.wastewater_emissions_per_capita);
  }

  if (myValue == null) return { value: null, percentile: null, stateMedian, comparisonCount: values.length };

  const below = values.filter((v) => v < myValue!).length;
  const percentile = below / values.length;

  return { value: myValue, percentile, stateMedian, comparisonCount: values.length };
}

// ─── Peer City Comparison ─────────────────────────────────────────────────────

export async function fetchWastewaterPeerCities(
  paddedGeofips: string,
  cityPopulation: number
): Promise<WastewaterPeerCity[]> {
  const key = `${paddedGeofips}:${Math.round(cityPopulation / 10000)}`;
  if (peerCache.has(key)) return peerCache.get(key)!;
  const promise = _fetchPeersUncached(paddedGeofips, cityPopulation);
  peerCache.set(key, promise);
  return promise;
}

async function _fetchPeersUncached(
  paddedGeofips: string,
  cityPopulation: number
): Promise<WastewaterPeerCity[]> {
  if (!cityPopulation || cityPopulation <= 0) return [];

  // Query v_wastewater_inventory_summary — avoids ghg_inventory_precalculated.population
  // which does not exist.  Estimate population from emissions / per_capita for filtering.
  const { data } = await supabaseClient
    .from('v_wastewater_inventory_summary')
    .select('geofips,jurisdiction_name,wastewater_emissions_mtco2e,wastewater_emissions_per_capita,best_tier')
    .eq('inventory_year', 2020)
    .not('wastewater_emissions_per_capita', 'is', null)
    .gt('wastewater_emissions_per_capita', 0)
    .not('wastewater_emissions_mtco2e', 'is', null)
    .gt('wastewater_emissions_mtco2e', 0)
    .limit(500);  // fetch broadly, then filter client-side by estimated population

  if (!data) return [];

  const minPop = cityPopulation * 0.4;
  const maxPop = cityPopulation * 2.5;

  return data
    .filter((r) => {
      if (r.geofips === paddedGeofips) return false;
      if (!r.wastewater_emissions_mtco2e || !r.wastewater_emissions_per_capita) return false;
      const estPop = Number(r.wastewater_emissions_mtco2e) / Number(r.wastewater_emissions_per_capita);
      return estPop >= minPop && estPop <= maxPop;
    })
    .slice(0, 8)
    .map((r) => {
      const total = Number(r.wastewater_emissions_mtco2e ?? 0);
      const perCap = Number(r.wastewater_emissions_per_capita ?? 0);
      const estPop = perCap > 0 ? Math.round(total / perCap) : null;
      return {
        location_name: String(r.jurisdiction_name ?? ''),
        population: estPop,
        wastewater_per_capita: perCap,
        wastewater_total: total,
        data_tier: r.best_tier != null ? Number(r.best_tier) : null,
        geofips: String(r.geofips),
      };
    });
}

// ─── Treatment Activity Breakdown ─────────────────────────────────────────────

export async function fetchWastewaterTreatmentBreakdown(
  paddedGeofips: string
): Promise<WastewaterTreatmentPoint[]> {
  if (treatmentCache.has(paddedGeofips)) return treatmentCache.get(paddedGeofips)!;
  const promise = _fetchTreatmentUncached(paddedGeofips);
  treatmentCache.set(paddedGeofips, promise);
  return promise;
}

async function _fetchTreatmentUncached(
  paddedGeofips: string
): Promise<WastewaterTreatmentPoint[]> {
  // Query wastewater_activity for treatment_level breakdown
  const { data } = await supabaseClient
    .from('wastewater_activity')
    .select('treatment_level,treatment_volume_af,attribution_share')
    .eq('geofips', paddedGeofips)
    .eq('inventory_year', 2020)
    .not('treatment_level', 'is', null);

  if (!data || data.length === 0) return [];

  // Aggregate by treatment_level
  const agg = new Map<string, { volume: number; share: number; count: number }>();
  for (const r of data) {
    const key = String(r.treatment_level ?? 'Unknown');
    const vol = Number(r.treatment_volume_af ?? 0);
    const share = Number(r.attribution_share ?? 1.0);
    const existing = agg.get(key) ?? { volume: 0, share: 0, count: 0 };
    agg.set(key, {
      volume: existing.volume + vol * share,
      share: existing.share + share,
      count: existing.count + 1,
    });
  }

  // Also get emissions from wastewater_emissions grouped by ghg_source
  const { data: emissionsData } = await supabaseClient
    .from('wastewater_emissions')
    .select('ghg_source,quantity_mtco2e')
    .eq('geofips', paddedGeofips)
    .eq('inventory_year', 2020)
    .not('quantity_mtco2e', 'is', null);

  const totalEmissions = (emissionsData ?? []).reduce(
    (sum, r) => sum + Number(r.quantity_mtco2e ?? 0),
    0
  );

  const totalVolume = Array.from(agg.values()).reduce((s, v) => s + v.volume, 0);
  if (totalVolume === 0) return [];

  return Array.from(agg.entries())
    .map(([level, val]) => ({
      treatment_level: formatTreatmentLevel(level),
      volume_af: val.volume,
      emissions_mtco2e: totalVolume > 0 ? totalEmissions * (val.volume / totalVolume) : 0,
      share: totalVolume > 0 ? val.volume / totalVolume : 0,
    }))
    .sort((a, b) => b.volume_af - a.volume_af);
}

function formatTreatmentLevel(raw: string): string {
  const map: Record<string, string> = {
    secondary_disinfected: 'Secondary',
    secondary: 'Secondary',
    advanced_secondary: 'Advanced Secondary',
    tertiary: 'Tertiary / Advanced',
    tertiary_recycled: 'Tertiary (Recycled)',
    lagoon: 'Lagoon / Pond',
    ocean_outfall: 'Ocean Outfall',
    recycled_water: 'Recycled Water',
    primary: 'Primary',
    default: 'Statewide Default',
  };
  return map[raw.toLowerCase()] ?? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function clearWastewaterAnalyticsCache(): void {
  timeSeriesCache.clear();
  percentileCache.clear();
  peerCache.clear();
  treatmentCache.clear();
}
